import { featureDirectoryFromNamespace, featureNamespaceFromDirectory } from './featureNamespace'
import { i18n } from './setup'
import type { SupportedLocale } from './types'

// Module 04 — the message-loading protocol (contract 04 §6). Two globs make "add a language
// = drop a file, no refactor" true:
//
//  • reserved namespaces (`common.en.json`, `ui.en.json`, …) are tiny and needed on the first
//    paint (error toasts, buttons, sr-only labels) → loaded EAGERLY, never behind a network
//    chunk. Authored in ORP-18597; the glob picks up whatever exists.
//  • per-feature bundles (`features/<f>/i18n/<locale>.json`) are LAZY factories → each locale's
//    feature copy code-splits into the feature's chunk.
//
// Namespace nesting is enforced at MERGE time: a feature file authors only its inner tree and
// the loader nests it under the feature key, so a feature bundle can never write into
// `common.*`/`errors.*` even by accident (contract §6).

interface JsonModule {
  default: Record<string, unknown>
}

const reserved = import.meta.glob<JsonModule>('./messages/*.*.json', { eager: true })
const feature = import.meta.glob<JsonModule>('/src/features/*/i18n/*.json')

// `<namespace>.<locale>.json` — namespace and locale each disallow `.` and `/`.
const RESERVED_FILE = /\/([^./]+)\.([^./]+)\.json$/
// `/features/<feature>/i18n/<locale>.json` — only the feature segment is captured; the locale
// is already known from the caller's filter.
const FEATURE_FILE = /\/features\/([^./]+)\/i18n\/[^./]+\.json$/

// Per-feature-per-locale merge de-duplication for `registerFeatureMessages`.
const mergedFeatures = new Set<string>()

export { featureDirectoryFromNamespace, featureNamespaceFromDirectory } from './featureNamespace'

/** Collect the eager reserved namespaces for one locale into a single `{ ns: tree }` object.
 *  Pure (no i18n mutation) so `setup` could seed with it and tests can assert nesting. */
export function getReservedMessages(locale: SupportedLocale): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [path, mod] of Object.entries(reserved)) {
    const match = RESERVED_FILE.exec(path)
    if (!match) continue
    const [, namespace, fileLocale] = match
    if (!namespace || fileLocale !== locale) continue
    out[namespace] = mod.default
  }
  return out
}

/** Lazily load + merge every bundle for one locale: reserved namespaces (from memory) plus
 *  each per-feature bundle nested under its feature key. Idempotent at the vue-i18n merge
 *  level (re-merging the same tree is a no-op overwrite). */
export async function loadLocaleMessages(locale: SupportedLocale): Promise<void> {
  const reservedMessages = getReservedMessages(locale)
  if (Object.keys(reservedMessages).length > 0) {
    i18n.global.mergeLocaleMessage(locale, reservedMessages)
  }

  const jobs = Object.entries(feature)
    .filter(([path]) => path.endsWith(`/i18n/${locale}.json`))
    .map(async ([path, load]) => {
      const match = FEATURE_FILE.exec(path)
      if (!match) return
      const featureDirectory = match[1]
      if (!featureDirectory) return
      const featureName = featureNamespaceFromDirectory(featureDirectory)
      const mod = await load()
      i18n.global.mergeLocaleMessage(locale, { [featureName]: mod.default })
      mergedFeatures.add(`${featureName}:${locale}`)
    })

  await Promise.all(jobs)
}

/** Merge ONE feature's bundle for ONE locale — the per-feature lazy-route trigger
 *  (contract §6 option b). No-op if already merged, or if the feature ships no bundle for
 *  that locale yet (it falls back to `en` per-key until its `<locale>.json` is dropped in). */
export async function registerFeatureMessages(
  featureName: string,
  locale: SupportedLocale,
): Promise<void> {
  const token = `${featureName}:${locale}`
  if (mergedFeatures.has(token)) return

  const featureDirectory = featureDirectoryFromNamespace(featureName)
  const suffix = `/features/${featureDirectory}/i18n/${locale}.json`
  const entry = Object.entries(feature).find(([path]) => path.endsWith(suffix))
  if (!entry) return

  const mod = await entry[1]()
  i18n.global.mergeLocaleMessage(locale, { [featureName]: mod.default })
  mergedFeatures.add(token)
}
